<template>
  <div v-if="showModal" :class="$style.modalOverlay" @click="closeModal">
    <div :class="$style.modalContentLarge" @click.stop>
      <div :class="$style.modalHeader">
        <h3 :class="$style.modalTitle">{{ translate(effectiveTitle) }}</h3>
        <label :class="$style.timeZoneControl">
          <span>{{ translate("session-time-zone-label") }}</span>
          <select v-model="selectedTimeZone" :class="$style.timeZoneSelect" @change="onTimeZoneChange">
            <option v-for="zone in timeZoneOptions" :key="zone" :value="zone">
              {{ zone }}
            </option>
          </select>
          <span :class="$style.timeZoneSourceNote">
            {{ translate("session-time-zone-source-note") }}
          </span>
        </label>
        <div :class="$style.headerRight">
          <button
            v-if="showBookmarkOption"
            :class="[$style.filterHeaderBtn, showBookmarkedOnly && $style.bookmarkBtnActive]"
            @click="showBookmarkedOnly = !showBookmarkedOnly"
          >
            {{ translate("session-bookmark-toggle") }}
          </button>
          <button
            v-if="showFilters"
            :class="$style.filterHeaderBtn"
            @click="showFiltersPanel = true"
          >
            {{ translate("session-filter-button") }}
          </button>
          <button :class="$style.closeButton" @click="closeModal">
            <img :src="closeIcon" :alt="translate('close')" :class="$style.closeIcon" />
          </button>
        </div>
      </div>
      <div :class="$style.modalBody">
        <div v-if="loading" :class="$style.loading">{{ translate("session-loading-games") }}</div>
        <div v-else-if="error" :class="$style.error">{{ error }}</div>
        <div v-else-if="displaySessions.length === 0" :class="$style.empty">{{ translate("session-no-games-found") }}</div>
        <div v-else :class="$style.gamesTable">
          <table :class="$style.table">
            <thead>
              <tr>
                <th v-if="showBookmarkOption" :class="$style.bookmarkCol">{{ translate("session-bookmark-column") }}</th>
                <th v-for="column in visibleColumns" :key="column.key">
                  {{ translate(column.label) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="session in displaySessions" :key="session.gameId" :class="$style.clickableRow" @click="viewSessionDetail(session)">
                <td v-if="showBookmarkOption" :class="$style.bookmarkCol">
                  <button
                    type="button"
                    :class="$style.bookmarkIconBtn"
                    :title="session.bookmarked ? translate('session-remove-bookmark') : translate('session-add-bookmark')"
                    @click.stop="toggleBookmark(session)"
                  >
                    <span v-if="session.bookmarked" :class="$style.bookmarkIcon">★</span>
                    <span v-else :class="$style.bookmarkIconEmpty">☆</span>
                  </button>
                </td>
                <td v-for="column in visibleColumns" :key="column.key">
                  <div v-if="column.key === 'action'" :class="$style.sessionActions">
                    <button :class="$style.viewDetailBtn" @click.stop="viewSessionDetail(session)">
                      {{ translate("session-view-detail") }}
                    </button>
                    <button v-if="canReplay(session)" :class="$style.replayBtn" :title="translate('replay.openWindow')" @click.stop="openReplay(session)">
                      {{ translate("replay.open") }}
                    </button>
                  </div>
                  <template v-else>
                    {{ getColumnValue(session, column.key) }}
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div :class="$style.modalFooter">
        <button :class="$style.closeBtn" @click="closeModal">{{ translate("close") }}</button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="showFiltersPanel && showFilters" :class="$style.filterPopupOverlay" @click.self="showFiltersPanel = false">
      <div :class="$style.filterPopupModal">
        <h3 :class="$style.filterPopupTitle">{{ translate("session-filter-title") }}</h3>
        <div :class="$style.filtersBar">
          <div v-if="showHostGameFilters" :class="$style.filterSection">
            <div :class="$style.filterSectionTitle">{{ translate("session-filter-host-game") }}</div>
            <div :class="$style.filterRow">
              <template v-if="visibleFilterSet.has('host')">
                <label :class="$style.filterLabel">{{ translate("session-filter-account") }}</label>
                <div :class="$style.accountSearch">
                  <input
                    v-model="filters.host"
                    type="search"
                    autocomplete="off"
                    role="combobox"
                    aria-autocomplete="list"
                    :aria-expanded="showHostSuggestions"
                    :placeholder="translate('session-filter-account-placeholder')"
                    :class="$style.filterInput"
                    @focus="showHostSuggestions = true"
                    @input="onHostSearchInput"
                    @keydown.esc="showHostSuggestions = false"
                    @blur="closeHostSuggestions"
                  />
                  <div v-if="showHostSuggestions" :class="$style.accountSuggestions" role="listbox">
                    <button
                      v-for="option in hostAccountOptions"
                      :key="option.key"
                      type="button"
                      role="option"
                      :class="$style.accountSuggestion"
                      @mousedown.prevent="selectHostAccount(option)"
                    >
                      {{ option.label }}
                    </button>
                    <div v-if="hostAccountOptions.length === 0" :class="$style.accountSuggestionEmpty">
                      {{ translate("session-no-games-found") }}
                    </div>
                  </div>
                </div>
              </template>
              <template v-if="visibleFilterSet.has('joinCode')">
                <label :class="$style.filterLabel">{{ translate("session-column-join-code") }}</label>
                <input v-model="filters.joinCode" type="text" :placeholder="translate('session-filter-join-code-placeholder')" :class="$style.filterInput" />
              </template>
              <template v-if="visibleFilterSet.has('gameId')">
                <label :class="$style.filterLabel">{{ translate("session-column-game-id") }}</label>
                <input v-model.number="filters.gameId" type="number" :placeholder="translate('session-filter-game-id-placeholder')" :class="$style.filterInput" />
              </template>
            </div>
          </div>

          <div v-if="visibleFilterSet.has('discipline')" :class="$style.filterSection">
            <div :class="$style.filterSectionTitle">{{ translate("discipline") }}</div>
            <div :class="$style.filterRow">
              <label :class="$style.filterLabel">{{ translate("discipline") }}</label>
              <select v-model="filters.discipline" :class="$style.filterSelect">
                <option value="">{{ translate("session-filter-all-disciplines") }}</option>
                <option v-for="opt in disciplineOptions" :key="opt.selectionValue" :value="opt.selectionValue">{{ opt.label }}</option>
              </select>
            </div>
          </div>

          <div v-if="visibleFilterSet.has('date')" :class="$style.filterSection">
            <div :class="$style.filterSectionTitle">{{ translate("session-filter-date") }}</div>
            <div :class="$style.filterRow">
              <div :class="$style.dateModeGroup">
                <label :class="$style.radioLabel">
                  <input v-model="filters.dateMode" type="radio" value="single" :class="$style.radioInput" />
                  {{ translate("session-filter-single-date") }}
                </label>
                <label :class="$style.radioLabel">
                  <input v-model="filters.dateMode" type="radio" value="range" :class="$style.radioInput" />
                  {{ translate("session-filter-date-range") }}
                </label>
              </div>
              <template v-if="filters.dateMode === 'single'">
                <label :class="$style.filterLabel">{{ translate("session-filter-date") }}</label>
                <input v-model="filters.singleDate" type="date" :class="$style.filterInput" />
              </template>
              <template v-else>
                <label :class="$style.filterLabel">{{ translate("session-filter-from") }}</label>
                <input v-model="filters.dateFrom" type="date" :class="$style.filterInput" />
                <label :class="$style.filterLabel">{{ translate("session-filter-to") }}</label>
                <input v-model="filters.dateTo" type="date" :class="$style.filterInput" />
              </template>
            </div>
          </div>
        </div>
        <div :class="$style.filterPopupActions">
          <button :class="$style.clearFiltersBtn" @click="clearFilters">{{ translate("session-filter-clear") }}</button>
          <button :class="$style.filterPopupCancelBtn" @click="showFiltersPanel = false">{{ translate("cancel") }}</button>
          <button :class="$style.applyFiltersBtn" @click="closeFilterAndApply">{{ translate("apply") }}</button>
        </div>
      </div>
    </div>
  </Teleport>

  <SessionDetailModal
    v-if="showDetailModal"
    :showModal="showDetailModal"
    :session="selectedSession"
    :closeModal="closeDetailModal"
    :viewerRole="viewerRole"
    :accessScope="viewPreset"
    :timeZone="selectedTimeZone"
  />
</template>

<script>
import styles from "@/css/SessionTrackingModal.module.css";
import CloseIcon from "@/assets/icons/close-red.svg";
import gameService from "@/services/game.service";
import { toast } from "vue3-toastify";
import SessionDetailModal from "./SessionDetailModal.vue";
import { disciplineOptions } from "@/utils/data";
import { APP_ROUTE_NAMES, USER_ROLES } from "@/utils/permissions";
import { isReplayComplete, openReplayWindow } from "@/utils/replay";
import {
  createSessionFilterState,
  getSessionColumns,
  getSessionFilterFields,
  getSessionStartDateKey,
  resolveSessionViewPreset,
} from "@/utils/sessionViews";
import {
  formatSessionTimestamp,
  getTrackingTimeZoneOptions,
  loadTrackingTimeZone,
  saveTrackingTimeZone,
  shiftDateKey,
} from "@/utils/sessionDateTime";
import {
  fuzzyTextMatch,
  getHostAccountLabel,
} from "@/utils/sessionSearch";

export default {
  name: "SessionTrackingModal",
  components: {
    SessionDetailModal,
  },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    closeModal: {
      type: Function,
      required: true,
    },
    myGamesOnly: {
      type: Boolean,
      default: false,
    },
    publicGamesOnly: {
      type: Boolean,
      default: false,
    },
    sharedWithMeOnly: {
      type: Boolean,
      default: false,
    },
    modalTitle: {
      type: String,
      default: "",
    },
    viewerRole: {
      type: String,
      default: USER_ROLES.ADMIN,
    },
  },
  data() {
    return {
      sessions: [],
      hostAccounts: [],
      loading: false,
      error: null,
      closeIcon: CloseIcon,
      showDetailModal: false,
      selectedSession: null,
      disciplineOptions,
      filters: createSessionFilterState(),
      showFiltersPanel: false,
      showBookmarkedOnly: false,
      showHostSuggestions: false,
      selectedTimeZone: loadTrackingTimeZone(),
      timeZoneOptions: getTrackingTimeZoneOptions(),
    };
  },
  computed: {
    $style() {
      return styles;
    },
    viewPreset() {
      return resolveSessionViewPreset({
        viewerRole: this.viewerRole,
        myGamesOnly: this.myGamesOnly,
        publicGamesOnly: this.publicGamesOnly,
        sharedWithMeOnly: this.sharedWithMeOnly,
      });
    },
    visibleColumns() {
      return getSessionColumns(this.viewPreset);
    },
    visibleFilterSet() {
      return new Set(getSessionFilterFields(this.viewPreset));
    },
    effectiveTitle() {
      if (this.modalTitle) return this.modalTitle;
      if (this.myGamesOnly) return "session-modal-title-my-games";
      if (this.publicGamesOnly) return "session-modal-title-public-games";
      if (this.sharedWithMeOnly) return "session-modal-title-shared-with-me";
      return "session-modal-title-game-tracking";
    },
    showFilters() {
      return this.visibleFilterSet.size > 0;
    },
    showHostGameFilters() {
      return ["host", "joinCode", "gameId"].some((field) => this.visibleFilterSet.has(field));
    },
    showBookmarkOption() {
      return this.myGamesOnly || this.publicGamesOnly;
    },
    displaySessions() {
      let list = this.filterSessionsClient(this.sessions);
      if (this.showBookmarkedOnly) list = list.filter((s) => s.bookmarked === true);
      return list;
    },
    hostAccountOptions() {
      const source = this.hostAccounts.length > 0
        ? this.hostAccounts.map((account) => ({
            hostUserId: account.id,
            hostEmail: account.email,
            hostName: account.name,
          }))
        : this.sessions;
      const unique = new Map();
      source.forEach((session) => {
        if (!session?.hostEmail && !session?.hostName && !session?.hostUsername) return;
        const label = getHostAccountLabel(session);
        const key = String(session.hostUserId || session.hostEmail || label);
        if (!unique.has(key)) unique.set(key, { key, label, hostUserId: session.hostUserId || null, hostEmail: session.hostEmail || null });
      });
      const query = this.filters.host || "";
      return Array.from(unique.values())
        .filter((option) => fuzzyTextMatch(option.label, query))
        .sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: "base" }))
        .slice(0, 100);
    },
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        this.fetchHostAccounts();
        this.fetchSessions();
      } else {
        this.sessions = [];
        this.hostAccounts = [];
        this.error = null;
        this.showFiltersPanel = false;
        this.showBookmarkedOnly = false;
        this.showHostSuggestions = false;
      }
    },
  },
  methods: {
    translate(key, params = {}) {
      return this.$t(key, params);
    },
    disciplineLabel(selectionValue) {
      if (!selectionValue) return "—";
      const opt = this.disciplineOptions.find((o) => o.selectionValue === selectionValue);
      return opt ? opt.label : selectionValue;
    },
    getColumnValue(session, columnKey) {
      if (columnKey === "discipline") return this.disciplineLabel(session.discipline);
      if (columnKey === "startTime" || columnKey === "endTime") {
        return session?.[columnKey]
          ? formatSessionTimestamp(session[columnKey], this.selectedTimeZone)
          : this.translate("session-na");
      }
      return session?.[columnKey] || this.translate("session-na");
    },
    onTimeZoneChange() {
      saveTrackingTimeZone(this.selectedTimeZone);
    },
    selectHostAccount(option) {
      this.filters.host = option.hostEmail || option.label;
      this.filters.hostUserId = option.hostUserId;
      this.showHostSuggestions = false;
    },
    onHostSearchInput() {
      this.filters.hostUserId = null;
      this.showHostSuggestions = true;
    },
    closeHostSuggestions() {
      window.setTimeout(() => {
        this.showHostSuggestions = false;
      }, 100);
    },
    applyFilters() {
      this.showHostSuggestions = false;
      if (!this.myGamesOnly && !this.publicGamesOnly && !this.sharedWithMeOnly) {
        this.fetchSessions();
      }
    },
    closeFilterAndApply() {
      this.showFiltersPanel = false;
      this.applyFilters();
    },
    async toggleBookmark(session) {
      const gameId = session.gameId;
      const currentlyBookmarked = !!session.bookmarked;
      try {
        if (currentlyBookmarked) {
          const res = await gameService.removeBookmark(gameId);
          if (res.status === 200 && res.data && res.data.success) {
            const s = this.sessions.find((x) => x.gameId === gameId);
            if (s) s.bookmarked = false;
          } else {
            toast.error(res.data?.message || this.translate("session-remove-bookmark-failed"));
          }
        } else {
          const res = await gameService.addBookmark(gameId);
          if (res.status === 200 && res.data && res.data.success) {
            const s = this.sessions.find((x) => x.gameId === gameId);
            if (s) s.bookmarked = true;
          } else {
            toast.error(res.data?.message || this.translate("session-add-bookmark-failed"));
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || this.translate("session-bookmark-update-failed"));
      }
    },
    clearFilters() {
      this.filters = createSessionFilterState();
      this.showHostSuggestions = false;
      if (!this.myGamesOnly && !this.publicGamesOnly && !this.sharedWithMeOnly) {
        this.fetchSessions();
      }
    },
    filterSessionsClient(list) {
      if (!list || !list.length) return [];
      const f = this.filters;
      let out = list.slice();
      if (this.visibleFilterSet.has("host") && f.host && f.host.trim()) {
        const searchedEmail = f.host.trim().toLowerCase();
        out = out.filter((session) => {
          const hostEmail = (session.hostEmail || "").trim().toLowerCase();
          return hostEmail === searchedEmail;
        });
      }
      if (this.visibleFilterSet.has("joinCode") && f.joinCode && f.joinCode.trim()) {
        const q = f.joinCode.trim();
        out = out.filter((s) => (s.joinCode || "").toString().includes(q));
      }
      if (this.visibleFilterSet.has("gameId") && f.gameId) {
        out = out.filter((s) => Number(s.gameId) === Number(f.gameId));
      }
      if (this.visibleFilterSet.has("discipline") && f.discipline && f.discipline.trim()) {
        out = out.filter(
          (s) => (s.discipline || "") === f.discipline.trim()
        );
      }

      if (this.visibleFilterSet.has("date") && f.dateMode === "single" && f.singleDate) {
        out = out.filter(
          (s) =>
            getSessionStartDateKey(s.startTime, this.selectedTimeZone) ===
            f.singleDate
        );
      } else if (this.visibleFilterSet.has("date") && f.dateMode === "range" && (f.dateFrom || f.dateTo)) {
        out = out.filter((s) => {
          const start = getSessionStartDateKey(
            s.startTime,
            this.selectedTimeZone
          );
          if (f.dateFrom && start < f.dateFrom) return false;
          if (f.dateTo && start > f.dateTo) return false;
          return true;
        });
      }
      return out;
    },
    buildSessionParams() {
      const params = {};
      if (this.filters.hostUserId) params.host_user_id = this.filters.hostUserId;
      if (this.filters.joinCode && this.filters.joinCode.trim()) {
        params.join_code = this.filters.joinCode.trim();
      }
      if (this.filters.gameId) params.game_id = this.filters.gameId;
      if (this.filters.discipline && this.filters.discipline.trim()) {
        params.discipline = this.filters.discipline.trim();
      }
      if (this.filters.dateMode === "single" && this.filters.singleDate) {
        params.date_from = shiftDateKey(this.filters.singleDate, -1);
        params.date_to = shiftDateKey(this.filters.singleDate, 1);
      } else if (this.filters.dateMode === "range") {
        if (this.filters.dateFrom) params.date_from = shiftDateKey(this.filters.dateFrom, -1);
        if (this.filters.dateTo) params.date_to = shiftDateKey(this.filters.dateTo, 1);
      }
      return params;
    },
    async fetchHostAccounts() {
      if (this.myGamesOnly || this.publicGamesOnly || this.sharedWithMeOnly) return;
      try {
        const response = await gameService.getSessionHostAccounts();
        this.hostAccounts = response?.data?.success && Array.isArray(response.data.accounts)
          ? response.data.accounts
          : [];
      } catch {
        this.hostAccounts = [];
      }
    },
    async fetchSessions() {
      this.loading = true;
      this.error = null;

      try {
        let res;
        if (this.sharedWithMeOnly) {
          res = await gameService.getSharedWithMe();
        } else if (this.publicGamesOnly) {
          res = await gameService.getPublicSessions();
        } else if (this.myGamesOnly) {
          res = await gameService.getMySessions();
        } else {
          res = await gameService.getAllSessions(this.buildSessionParams());
        }

        if (this.sharedWithMeOnly) {
          if (res && res.success && Array.isArray(res.sessions)) {
            this.sessions = res.sessions;
          } else {
            this.sessions = [];
            this.error = res?.message || this.translate("session-load-shared-games-failed");
            if (res && !res.success) toast.error(this.error);
          }
        } else if (res && res.status === 200 && res.data) {
          if (res.data.success && Array.isArray(res.data.sessions)) {
            this.sessions = res.data.sessions;
          } else {
            this.error = res.data.message || this.translate("session-fetch-games-failed");
            toast.error(this.error);
          }
        } else {
          this.error = res?.data?.message || this.translate("session-fetch-games-failed");
          toast.error(this.error);
        }
      } catch (err) {
        this.error = err.message || this.translate("session-fetch-games-error");
        toast.error(this.error);
      } finally {
        this.loading = false;
      }
    },
    canReplay(session) {
      return isReplayComplete(session);
    },
    openReplay(session) {
      const { href } = this.$router.resolve({ name: APP_ROUTE_NAMES.GAME_REPLAY, params: { gameId: session.gameId } });
      if (!openReplayWindow(href, session.gameId)) {
        toast.error(this.translate("replay.popupBlocked"));
      }
    },
    viewSessionDetail(session) {
      this.selectedSession = { ...session };
      this.showDetailModal = true;
    },
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedSession = null;
    },
  },
};
</script>

<style module>
</style>
